// The share tags (Open Graph + Twitter) of every public page, from the page data the server already built. Pure: no Vue,
// no server imports, so tests/ogTags.test.js runs it under plain node. entry-server.js turns the result into <meta> tags.
import {absoluteUrl, mediaIdOf} from "./mediaUrl.js";
import {sizedUrl, canSize} from "./photoSizes.js";
import {altFor} from "./imageAlt.js";

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const filled = (v) => typeof v === "string" && v.trim() !== "";
const clean = (v, max) => String(v ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, max);

// A photo address out of whatever a page stored: a URL, or an image object (url / pexels.src.large).
export function photoUrlOf(v) {
    if (filled(v)) return v.trim();
    if (!isObject(v)) return "";
    if (filled(v.url)) return v.url.trim();
    if (isObject(v.src) && filled(v.src.large)) return v.src.large;
    if (isObject(v.pexels) && isObject(v.pexels.src) && filled(v.pexels.src.large)) return v.pexels.src.large;
    return "";
}

// The first photo a section shows: its image, a video's poster, a cover, then any list of pictures it holds.
function sectionPhoto(section) {
    if (!isObject(section) || section.hidden === true) return "";
    const direct = photoUrlOf(section.image) || photoUrlOf(section.cover) || photoUrlOf(section.photo)
        || photoUrlOf(section.video?.poster) || photoUrlOf(section.video?.image);
    if (direct) return direct;
    for (const key of ["images", "items", "photos", "projects", "posts", "products"]) {
        const list = section[key];
        if (!Array.isArray(list)) continue;
        for (const item of list) {
            const url = photoUrlOf(item?.image) || photoUrlOf(item?.cover) || photoUrlOf(item?.photo) || (isObject(item) ? "" : photoUrlOf(item));
            if (url) return url;
        }
    }
    return "";
}

// og:image: the page's own pick (post cover, product photo, ...), else its hero photo, else the first photo on the page,
// else the site logo. Always the 1200 wide copy when it can be sized.
export function ogImage(pageData) {
    const sections = Array.isArray(pageData?.sections) ? pageData.sections : [];
    const hero = sections.find((s) => isObject(s) && s.type === "hero" && s.hidden !== true);
    const candidates = [
        photoUrlOf(pageData?.pageImage),
        hero ? sectionPhoto(hero) : "",
        ...sections.map(sectionPhoto),
        photoUrlOf(pageData?.companyLogo),
    ];
    const url = candidates.find(filled) || "";
    return canSize(url) ? sizedUrl(url, 1200) : url;
}

// {title, description, url, image, width, height, alt, type, siteName} for the page.
export function ogData({pageData, origin, path, alts}) {
    const siteName = clean(pageData?.companyName, 120);
    const first = (Array.isArray(pageData?.sections) ? pageData.sections : []).find((s) => isObject(s) && s.hidden !== true) || {};
    const title = clean(pageData?.pageTitle, 160) || siteName;
    const description = clean(
        pageData?.metaDescription || pageData?.pageDescription || first.content || first.paragraph || first.description || first.subtitle || first.title || siteName,
        300,
    );
    const rawImage = ogImage(pageData);
    const image = absoluteUrl(rawImage, origin);
    // A sized copy is at most 1200 wide; its height depends on the photo, which we do not know here.
    const width = rawImage && /\/1200\.webp$/.test(rawImage) ? 1200 : undefined;
    const alt = clean(altFor(alts ?? pageData?.imageAlts, photoUrlOf(pageData?.pageImage) || rawImage, first.title, title), 200);
    let type = "website";
    if (pageData?.pageType === "post") type = "article";
    else if (pageData?.shop?.kind === "product") type = "product";
    return {title, description, url: `${origin}${path}`, image, width, height: undefined, alt, type, siteName};
}

// The <meta> entries (property/name + content), empty ones left out.
export function ogMeta(og) {
    const out = [
        {property: "og:title", content: og.title},
        {property: "og:description", content: og.description},
        {property: "og:url", content: og.url},
        {property: "og:type", content: og.type},
        {property: "og:site_name", content: og.siteName},
        {property: "og:locale", content: "en_GB"},
        {name: "twitter:card", content: og.image ? "summary_large_image" : "summary"},
        {name: "twitter:title", content: og.title},
        {name: "twitter:description", content: og.description},
    ];
    if (og.image) {
        out.push({property: "og:image", content: og.image});
        if (og.image.startsWith("https://")) out.push({property: "og:image:secure_url", content: og.image});
        if (og.width) out.push({property: "og:image:width", content: String(og.width)});
        if (og.height) out.push({property: "og:image:height", content: String(og.height)});
        if (og.alt) out.push({property: "og:image:alt", content: og.alt}, {name: "twitter:image:alt", content: og.alt});
        out.push({name: "twitter:image", content: og.image});
    }
    return out.filter((m) => filled(m.content));
}
